import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DashboardLayout } from '../layouts/DashboardLayout';
import useFetchClinicianByPracticeId from '../hooks/Clinic/useFetchClinicianByPractice';
import { XMarkIcon } from '@heroicons/react/24/outline';

import { useClinicianStatusManagement } from '../hooks/useClinicianStatusManagement';
import { ClinicianTable } from '../components/clinician/ClinicianTable';
import {
  Users,
  Search,
  Filter,
  UserX,
  Calendar,
  CheckCircle,
  Download,
  Settings,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import Preloader from '../components/ui/Preloader';
import CreateClinicianForm from '../components/clinician/CreateClinicianForm';
import { useDispatch, useSelector } from 'react-redux';
import { toggleClinicianStatus, bulkUpdate } from '../redux/clinic/clinicSlice';
import { AppDispatch } from '../redux';

// Use the imported Clinician type from types/index.ts
import { Clinician } from '../types';

interface BackendFilterState {
  search: string;
  listFilter: '' | 'active' | 'inactive';
  specialty: string;
  location: string;
  sortBy: string;
}

export const CliniciansPage: React.FC = () => {
  const practiceId = JSON.parse(localStorage.getItem('user') || '{}')?.["custom:id_practice"];
  const selectedPractice = useSelector(
    (state: { practices?: { selectedPractice?: { locations?: unknown[] } } }) =>
      state?.practices?.selectedPractice
  );

  const { practiceClinicians, isLoading, practiceCliniciansCounts, practiceCliniciansPagination, fetchClinicianData } = useFetchClinicianByPracticeId();
  const { handleClinicianStatusChange } = useClinicianStatusManagement();
  const dispatch = useDispatch<AppDispatch>();
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedClinicians, setSelectedClinicians] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [backendFilters, setBackendFilters] = useState<BackendFilterState>({
    search: '',
    listFilter: '',
    specialty: 'all',
    location: 'all',
    sortBy: 'name'
  });

  const [searchInput, setSearchInput] = useState('');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setBackendFilters((prev) => ({ ...prev, search: searchInput }));
      setPage(1);
    }, 400);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchInput]);

  const practiceLocationArray = useMemo(() => {
    const rawLocations = Array.isArray(selectedPractice?.locations) ? selectedPractice.locations : [];
    return rawLocations
      .map((loc: unknown) => {
        if (!loc) return '';
        if (typeof loc === 'string') return loc.trim();
        if (typeof loc !== 'object') return '';
        const locObj = loc as { city?: unknown; state?: unknown; zipCode?: unknown };
        const city = typeof locObj.city === 'string' ? locObj.city.trim() : '';
        const state = typeof locObj.state === 'string' ? locObj.state.trim() : '';
        const zip = typeof locObj.zipCode === 'string' ? locObj.zipCode.trim() : '';
        return [city, state, zip].filter(Boolean).join(', ');
      })
      .filter(Boolean);
  }, [selectedPractice]);

  const backendRequestOptions = useMemo(() => ({
    page,
    limit,
    ...(backendFilters.listFilter !== '' ? { isActive: backendFilters.listFilter === 'active' } : {}),
    ...(backendFilters.search.trim() ? { search: backendFilters.search.trim() } : {}),
    ...(backendFilters.specialty !== 'all' ? { specialty: backendFilters.specialty } : {}),
    // Backend expects location as array. When user picks one, pass one-item array.
    // Otherwise pass all logged-in practice locations (if available).
    ...((backendFilters.location !== 'all' || practiceLocationArray.length > 0)
      ? { location: backendFilters.location !== 'all' ? [backendFilters.location] : practiceLocationArray }
      : {}),
    ...(backendFilters.sortBy !== 'name' ? { sortBy: backendFilters.sortBy } : {})
  }), [page, limit, backendFilters, practiceLocationArray]);

  // Serialize options to a stable string so the effect only re-fires when
  // values actually change, not just when the object gets a new reference.
  const backendRequestOptionsKey = JSON.stringify(backendRequestOptions);

  useEffect(() => {
    if (practiceId) {
      fetchClinicianData(practiceId, backendRequestOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchClinicianData, practiceId, backendRequestOptionsKey]);

  // Normalize clinician data for consistent filtering (keeping your existing logic)
  const normalizedClinicians = useMemo(() => {
    if (!practiceClinicians || !Array.isArray(practiceClinicians)) {
      return [];
    }

    return practiceClinicians.map(clinician => {
      if (!clinician) return null;

      let specialties: string[] = [];
      if (Array.isArray(clinician.specialties)) {
        specialties = clinician.specialties.map(s =>
          typeof s === 'object' && s && 'name' in s ? (s as any).name :
            typeof s === 'string' ? s : String(s)
        ).filter(Boolean);
      } else if (typeof clinician.specialties === 'string') {
        specialties = [clinician.specialties];
      } else if (Array.isArray(clinician.specialty)) {
        specialties = clinician.specialty.map(s =>
          typeof s === 'object' && s && 'name' in s ? (s as any).name :
            typeof s === 'string' ? s : String(s)
        ).filter(Boolean);
      } else if (typeof clinician.specialty === 'string') {
        specialties = [clinician.specialty];
      }

      const name = clinician.name || `${clinician.firstName || (clinician as any).first_name || ''} ${clinician.lastName || (clinician as any).last_name || ''}`.trim();
      const firstName = (clinician.firstName || (clinician as any).first_name || clinician.name?.split(' ')[0] || '') as string;
      const lastName = (clinician.lastName || (clinician as any).last_name || clinician.name?.split(' ').slice(1).join(' ') || '') as string;
      const status = clinician.status || (clinician as any).verificationStatus || 'pending';

      return {
        ...clinician,
        name,
        firstName,
        lastName,
        specialties: specialties.filter(Boolean),
        email: clinician.email || '',
        status,
        location: clinician.address?.city ? `${clinician.address.city}, ${clinician.address.state}` : '',
        rating: clinician.rating || 0,
        lastActive: (clinician as any).lastActive || clinician.updatedAt || ''
      };
    }).filter(Boolean) as Clinician[];
  }, [practiceClinicians]);

  const filterOptions = useMemo(() => {
    const specialties = [...new Set(
      normalizedClinicians
        .flatMap((clinician) => clinician.specialties || [])
        .filter(Boolean)
        .map((specialty) => String(specialty))
    )].sort();

    const locations = [...new Set(
      normalizedClinicians
        .map((clinician) => clinician.location)
        .filter(Boolean)
        .map((location) => String(location))
    )].sort();

    return { specialties, locations };
  }, [normalizedClinicians]);

  // Keep backend order/data as-is; no frontend filters.
  const filteredAndSortedClinicians = useMemo(() => {
    return normalizedClinicians;
  }, [normalizedClinicians]);

  const handleBackendFilterChange = useCallback((key: keyof BackendFilterState, value: string) => {
    setBackendFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  const clearBackendFilters = useCallback(() => {
    setSearchInput('');
    setBackendFilters({
      search: '',
      listFilter: '',
      specialty: 'all',
      location: 'all',
      sortBy: 'name'
    });
    setPage(1);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchInput('');
    setBackendFilters((prev) => ({ ...prev, search: '' }));
    setPage(1);
  }, []);

  const toggleFilters = useCallback(() => {
    setShowFilters((prev) => !prev);
  }, []);

  const hasActiveFilters = useMemo(() => {
    return backendFilters.search.trim() !== '' ||
      backendFilters.listFilter !== '' ||
      backendFilters.specialty !== 'all' ||
      backendFilters.location !== 'all' ||
      backendFilters.sortBy !== 'name';
  }, [backendFilters]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (backendFilters.search.trim() !== '') count++;
    if (backendFilters.listFilter !== '') count++;
    if (backendFilters.specialty !== 'all') count++;
    if (backendFilters.location !== 'all') count++;
    if (backendFilters.sortBy !== 'name') count++;
    return count;
  }, [backendFilters]);

  const handleCreateClinician = async (clinicianData: any) => {
    try {
      // TODO: Implement API call to create clinician

      // For now, just close the form
      setShowCreateForm(false);

      // Refresh the data
      // await refetch();
    } catch (error) {
      console.error('Failed to create clinician:', error);
    }
  };

  const handleCancelCreate = () => {

  };

  // Bulk edit functions
  const handleBulkEdit = useCallback(async (updates: any) => {
    try {
      if (selectedClinicians.length === 0) {
        setSuccessMessage('Please select clinicians to edit');
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
        return;
      }

      // Format the request to match backend expectations
      // Backend expects: { clinicians: [{ id: '123', status: 'active' }, { id: '456', status: 'active' }] }
      const formattedClinicians = selectedClinicians.map(id => ({
        id,
        ...updates // Spread all update fields into each clinician object
      }));


      await dispatch(bulkUpdate({
        clinicians: formattedClinicians
      })).unwrap();

      setSuccessMessage(`Successfully updated ${selectedClinicians.length} clinician${selectedClinicians.length !== 1 ? 's' : ''}`);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);

      // Clear selections and close modal
      setSelectedClinicians([]);
      setShowBulkEditModal(false);

      // Refresh data and keep current backend filter params
      await fetchClinicianData(practiceId, backendRequestOptions);
    } catch (error) {
      console.error('Bulk edit failed:', error);
      setSuccessMessage('Failed to update clinicians. Please try again.');
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    }
  }, [selectedClinicians, dispatch, practiceId, fetchClinicianData, backendRequestOptions]);

  const handleSelectClinician = (clinicianId: string) => {
    setSelectedClinicians(prev =>
      prev.includes(clinicianId)
        ? prev.filter(id => id !== clinicianId)
        : [...prev, clinicianId]
    );
  };

  const handleSelectAll = () => {
    if (selectedClinicians.length === filteredAndSortedClinicians.length) {
      setSelectedClinicians([]);
    } else {
      setSelectedClinicians(filteredAndSortedClinicians.map(c => c._id || c.id).filter(Boolean) as string[]);
    }
  };

  // Get existing slugs for uniqueness check
  const existingSlugs = useMemo(() => {
    return normalizedClinicians.map(c => c.slug).filter(Boolean) as string[];
  }, [normalizedClinicians]);

  const handleStatusToggle = async (clinicianId: string, newStatus: 'active' | 'inactive') => {

    try {
      // Find the clinician data
      const clinician = practiceClinicians.find(c => c._id === clinicianId || c.id === clinicianId);

      if (!clinician) {
        console.error('Clinician not found:', clinicianId);
        return;
      }


      // Update the clinician status using the dedicated toggle endpoint

      // Dispatch the Redux action for single clinician toggle

      const result = await dispatch(toggleClinicianStatus({
        clinicianId,
        isActive: newStatus === 'active'
      })).unwrap();


      // Then handle campaign creation if activating
      if (newStatus === 'active') {
        const result = await handleClinicianStatusChange(clinician as any, 'active');
        if (result.success) {
          const clinicianName = clinician.fullName || clinician.name || `${clinician.firstName || ''} ${clinician.lastName || ''}`.trim();
          setSuccessMessage(`Clinician activated successfully! Advertising Partners campaign created for ${clinicianName}`);
          setShowSuccessMessage(true);
          setTimeout(() => setShowSuccessMessage(false), 5000);
        }
      } else {
        setSuccessMessage(`Clinician deactivated successfully!`);
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
      }

      // Refresh the clinician data and keep current backend filter params
      await fetchClinicianData(practiceId, backendRequestOptions);
    } catch (error) {
      console.error('Failed to toggle status:', error);
      // Show error message to user
      setSuccessMessage('Failed to update clinician status. Please try again.');
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    }
  };

  // Only block the whole page on first load.
  // During filter/search requests, keep UI mounted to avoid "page reload" flicker.
  if (isLoading && normalizedClinicians.length === 0) {
    return <Preloader />;
  }

  return (
    <DashboardLayout>
      {/* Background with gradient overlay */}
      <div className="fixed from-slate-50 via-blue-50/30 to-indigo-50/50 pointer-events-none" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="relative flex-1 p-4 md:p-8"
      >
        <div className="space-y-8 mx-auto">

          {/* Success Notification */}
          <AnimatePresence>
            {showSuccessMessage && (
              <motion.div
                initial={{ opacity: 0, y: -50 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -50 }}
                className="fixed top-4 right-4 bg-green-600 text-white px-6 py-4 rounded-lg shadow-lg z-50 max-w-md"
              >
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5" />
                  <div>
                    <p className="font-medium">Success!</p>
                    <p className="text-sm opacity-90">{successMessage}</p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Clinicians</h1>
              <p className="text-gray-600 mt-1 flex flex-wrap items-center gap-2">
                View and manage clinician info
                {practiceCliniciansCounts && (
                  <span className="font-medium text-gray-700">
                    · {practiceCliniciansCounts.active} active, {practiceCliniciansCounts.inactive} inactive, {practiceCliniciansCounts.total} total
                  </span>
                )}
                <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-sm font-medium text-emerald-800 ring-1 ring-inset ring-emerald-600/20">
                  {backendFilters.listFilter === 'active'
                    ? 'Active only'
                    : backendFilters.listFilter === 'inactive'
                      ? 'Inactive only'
                      : 'No status filter'}
                </span>
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowBulkEdit(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
              >
                <Settings className="w-4 h-4" />
                Bulk Edit
              </button>

              {/* Edit Selected Button - Only show when clinicians are selected */}
              {selectedClinicians.length > 0 && (
                <button
                  onClick={() => {
                    setShowBulkEdit(true);
                    setShowBulkEditModal(true);
                  }}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center gap-2"
                >
                  <Settings className="w-4 h-4" />
                  Edit Selected ({selectedClinicians.length})
                </button>
              )}

              <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2">
                <Download className="w-4 h-4" />
                Export
              </button>
            </div>
          </div>

          {/* Stats Cards - Enhanced with glassmorphic design */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6"
          >
            <StatCard
              title="Total Clinicians"
              value={practiceCliniciansCounts?.total || 0}
              icon={<Users className="w-7 h-7" />}
              gradient="from-blue-500/90 to-cyan-500/90"
              bgColor="bg-blue-50/50"
              delay={0.1}
            />
            <StatCard
              title="Active"
              value={practiceCliniciansCounts?.active || 0}
              icon={<Calendar className="w-7 h-7" />}
              gradient="from-emerald-500/90 to-teal-500/90"
              bgColor="bg-emerald-50/50"
              delay={0.2}
            />
            <StatCard
              title="Inactive"
              value={practiceCliniciansCounts?.inactive || 0}
              icon={<UserX className="w-7 h-7" />}
              gradient="from-slate-500/90 to-gray-500/90"
              bgColor="bg-slate-50/50"
              delay={0.4}
            />
          </motion.div>

          {/* Search and Filters - backend-driven */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="space-y-6"
          >
            <div className="flex flex-col xl:flex-row gap-6">
              <div className="flex-1 w-full">
                <div className="relative group">
                  <div className="absolute inset-0 bg-white/60 backdrop-blur-xl border border-white/30 rounded-2xl shadow-xl shadow-blue-500/5" />
                  <div className="relative">
                    <Search className="h-5 w-5 absolute left-5 top-4 text-slate-400 group-focus-within:text-blue-500 transition-colors duration-200" />
                    <input
                      type="text"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      placeholder="Search by name, email, specialty, or location..."
                      className="w-full pl-14 pr-14 py-4 bg-transparent border-0 placeholder-slate-400 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-200 text-slate-800 font-medium"
                    />
                    {searchInput && (
                      <motion.button
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        onClick={clearSearch}
                        className="absolute right-4 top-4 text-slate-400 hover:text-slate-600 transition-colors p-1 hover:bg-white/50 rounded-lg"
                        aria-label="Clear search"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </motion.button>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <motion.button
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={toggleFilters}
                  className="relative group"
                >
                  <div className={`absolute inset-0 rounded-2xl transition-all duration-200 ${showFilters || hasActiveFilters
                    ? 'bg-blue-500/20 backdrop-blur-xl border border-blue-300/50 shadow-lg shadow-blue-500/20'
                    : 'bg-white/60 backdrop-blur-xl border border-white/30 shadow-lg shadow-slate-500/5'
                    }`} />
                  <div className={`relative flex items-center px-6 py-4 font-semibold transition-all duration-200 ${showFilters || hasActiveFilters
                    ? 'text-blue-700'
                    : 'text-slate-700'
                    }`}>
                    <Filter className="mr-2" size={20} />
                    Filters
                    {hasActiveFilters && activeFilterCount > 0 && (
                      <motion.span
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="ml-3 px-2.5 py-1 bg-blue-500 text-white text-xs rounded-full font-bold shadow-lg"
                      >
                        {activeFilterCount}
                      </motion.span>
                    )}
                  </div>
                </motion.button>

                <AnimatePresence>
                  {hasActiveFilters && (
                    <motion.button
                      initial={{ opacity: 0, scale: 0.8, x: -20 }}
                      animate={{ opacity: 1, scale: 1, x: 0 }}
                      exit={{ opacity: 0, scale: 0.8, x: -20 }}
                      whileHover={{ scale: 1.02, y: -1 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={clearBackendFilters}
                      className="relative group"
                    >
                      <div className="absolute inset-0 bg-white/60 backdrop-blur-xl border border-white/30 rounded-2xl shadow-lg shadow-slate-500/5" />
                      <div className="relative flex items-center px-6 py-4 text-slate-700 font-semibold">
                        Clear All
                      </div>
                    </motion.button>
                  )}
                </AnimatePresence>
              </div>
            </div>

            <AnimatePresence>
              {showFilters && (
                <motion.div
                  initial={{ opacity: 0, height: 0, y: -20 }}
                  animate={{ opacity: 1, height: 'auto', y: 0 }}
                  exit={{ opacity: 0, height: 0, y: -20 }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                  className="relative overflow-hidden"
                >
                  <div className="absolute inset-0 bg-white/50 backdrop-blur-xl border border-white/30 rounded-3xl shadow-2xl shadow-blue-500/10" />
                  <div className="relative p-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                      <FilterSelect
                        label="Status"
                        value={backendFilters.listFilter}
                        onChange={(value) => handleBackendFilterChange('listFilter', value)}
                        options={[
                          { value: '', label: 'No status filter' },
                          { value: 'active', label: 'Active only' },
                          { value: 'inactive', label: 'Inactive only' }
                        ]}
                      />
                      <FilterSelect
                        label="Specialty"
                        value={backendFilters.specialty}
                        onChange={(value) => handleBackendFilterChange('specialty', value)}
                        options={[
                          { value: 'all', label: 'All Specialties' },
                          ...filterOptions.specialties.map((specialty) => ({
                            value: specialty,
                            label: specialty
                          }))
                        ]}
                      />
                      <FilterSelect
                        label="Location"
                        value={backendFilters.location}
                        onChange={(value) => handleBackendFilterChange('location', value)}
                        options={[
                          { value: 'all', label: 'All Locations' },
                          ...filterOptions.locations.map((location) => ({
                            value: location,
                            label: location
                          }))
                        ]}
                      />
                      <FilterSelect
                        label="Sort By"
                        value={backendFilters.sortBy}
                        onChange={(value) => handleBackendFilterChange('sortBy', value)}
                        options={[
                          { value: 'name', label: 'Full Name (A-Z)' },
                          { value: 'firstName', label: 'First Name (A-Z)' },
                          { value: 'lastName', label: 'Last Name (A-Z)' },
                          { value: 'email', label: 'Email (A-Z)' },
                          { value: 'status', label: 'Status' },
                          { value: 'lastActive', label: 'Last Active (Recent)' },
                          { value: 'rating', label: 'Rating (High to Low)' }
                        ]}
                      />
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Content Area - Enhanced glassmorphic container */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="relative"
          >
            <div className="absolute inset-0 bg-white/50 backdrop-blur-xl border border-white/30 rounded-3xl shadow-2xl shadow-blue-500/10" />
            <div className="relative overflow-hidden rounded-3xl">
              {filteredAndSortedClinicians.length === 0 ? (
                <EmptyState
                  totalCount={normalizedClinicians.length}
                />
              ) : (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                  {/* Bulk Edit Status Bar */}
                  {showBulkEdit && (
                    <div className="bg-blue-50 border-b border-blue-200 p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Settings className="w-5 h-5 text-blue-600" />
                          <span className="text-blue-800 font-medium">Bulk Edit Mode</span>
                          <span className="text-blue-600 text-sm">
                            {selectedClinicians.length} clinician{selectedClinicians.length !== 1 ? 's' : ''} selected
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {selectedClinicians.length > 0 && (
                            <button
                              onClick={() => {
                                setShowBulkEditModal(true);
                              }}
                              className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                            >
                              Edit Selected
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setShowBulkEdit(false);
                              setSelectedClinicians([]);
                            }}
                            className="px-3 py-1.5 text-blue-600 border border-blue-300 text-sm rounded-lg hover:bg-blue-50 transition-colors"
                          >
                            Exit Bulk Edit
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <ClinicianTable
                    clinicians={filteredAndSortedClinicians}
                    onStatusToggle={handleStatusToggle}
                    showBulkEdit={showBulkEdit}
                    selectedClinicians={selectedClinicians}
                    onSelectClinician={handleSelectClinician}
                    onSelectAll={handleSelectAll}
                  />

                  {/* Pagination */}
                  {(practiceCliniciansPagination != null || (practiceClinicians?.length ?? 0) > 0) && (
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-4 border-t border-gray-200 bg-gray-50/50">
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        {(() => {
                          const currentPage = practiceCliniciansPagination?.page ?? page;
                          const currentLimit = practiceCliniciansPagination?.limit ?? limit;
                          const total = practiceCliniciansPagination?.count ?? practiceClinicians?.length ?? 0;
                          const start = total === 0 ? 0 : (currentPage - 1) * currentLimit + 1;
                          const end = total === 0 ? 0 : Math.min(currentPage * currentLimit, total);
                          return (
                            <span>
                              Showing{' '}
                              <span className="font-medium text-gray-900">{start}{total > 0 ? `–${end}` : ''}</span>
                              {' '}of{' '}
                              <span className="font-medium text-gray-900">{total}</span>
                              {' '}clinicians
                            </span>
                          );
                        })()}
                        <label className="flex items-center gap-2">
                          <span>Rows per page</span>
                          <select
                            value={limit}
                            onChange={(e) => {
                              setLimit(Number(e.target.value));
                              setPage(1);
                            }}
                            className="rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            {[10, 25, 50].map((n) => (
                              <option key={n} value={n}>{n}</option>
                            ))}
                          </select>
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                          disabled={!practiceCliniciansPagination?.hasPrevPage && page <= 1}
                          className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronLeft className="w-4 h-4" />
                          Previous
                        </button>
                        <span className="px-3 py-2 text-sm text-gray-600">
                          Page <span className="font-medium text-gray-900">{practiceCliniciansPagination?.page ?? page}</span>
                          {typeof (practiceCliniciansPagination?.totalPages) === 'number'
                            ? ` of ${practiceCliniciansPagination.totalPages}`
                            : ''}
                        </span>
                        <button
                          type="button"
                          onClick={() => setPage((p) => p + 1)}
                          disabled={
                            practiceCliniciansPagination?.hasNextPage === false ||
                            (practiceCliniciansPagination?.totalPages != null && page >= practiceCliniciansPagination.totalPages) ||
                            (practiceCliniciansPagination == null && (practiceClinicians?.length ?? 0) < limit)
                          }
                          className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Next
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </div>

        {/* Create Clinician Form Modal */}
        <AnimatePresence>
          {showCreateForm && (
            <CreateClinicianForm
              onSave={handleCreateClinician}
              onCancel={handleCancelCreate}
              existingSlugs={existingSlugs}
            />
          )}
        </AnimatePresence>

        {/* Bulk Edit Modal */}
        <AnimatePresence>
          {showBulkEditModal && (
            <BulkEditModal
              isOpen={showBulkEditModal}
              onClose={() => setShowBulkEditModal(false)}
              onSave={handleBulkEdit}
              selectedCount={selectedClinicians.length}
            />
          )}
        </AnimatePresence>
      </motion.div>
    </DashboardLayout>
  );
};

// Enhanced Stat Card Component with glassmorphic design
const StatCard = React.memo<{
  title: string;
  value: number;
  icon: React.ReactNode;
  gradient: string;
  bgColor: string;
  delay: number;
}>(({ title, value, icon, gradient, bgColor, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ duration: 0.6, delay, ease: "easeOut" }}
    whileHover={{ scale: 1.02, y: -4 }}
    className="relative group cursor-pointer"
  >
    {/* Glassmorphic background */}
    <div className={`absolute inset-0 ${bgColor} backdrop-blur-xl border border-white/30 rounded-2xl shadow-xl shadow-blue-500/5 group-hover:shadow-2xl group-hover:shadow-blue-500/20 transition-all duration-300`} />

    {/* Content */}
    <div className="relative p-6 md:p-8">
      <div className="flex items-center gap-6">
        <motion.div
          initial={{ scale: 0.8, rotate: -10 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ duration: 0.5, delay: delay + 0.1 }}
          className={`p-4 rounded-2xl bg-gradient-to-br ${gradient} text-white shadow-lg`}
        >
          {icon}
        </motion.div>
        <div className="space-y-1">
          <p className="text-sm font-semibold text-slate-500 uppercase tracking-wide">{title}</p>
          <motion.p
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: delay + 0.2 }}
            className="text-3xl md:text-4xl font-bold text-slate-800"
          >
            {value}
          </motion.p>
        </div>
      </div>
    </div>
  </motion.div>
));

// Filter Select Component
const FilterSelect = React.memo<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}>(({ label, value, onChange, options }) => (
  <div className="space-y-3">
    <label className="block text-sm font-bold text-slate-700 uppercase tracking-wide">{label}</label>
    <div className="relative">
      <div className="absolute inset-0 bg-white/60 backdrop-blur-lg border border-white/40 rounded-xl shadow-lg" />
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="relative w-full px-4 py-3 bg-transparent border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-200 text-slate-800 font-medium appearance-none cursor-pointer"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value} className="bg-white text-slate-800">
            {option.label}
          </option>
        ))}
      </select>
      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
        <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  </div>
));

// Enhanced Empty State Component with glassmorphic design
const EmptyState = React.memo<{
  totalCount: number;
}>(({ totalCount }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6 }}
    className="flex flex-col items-center justify-center py-20 px-8 space-y-6"
  >
    {/* Icon with glassmorphic background */}
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.6, delay: 0.1 }}
      className="relative"
    >
      <div className="absolute inset-0 bg-slate-100/60 backdrop-blur-lg rounded-3xl shadow-xl" />
      <div className="relative p-8 bg-gradient-to-br from-slate-50/50 to-blue-50/50 rounded-3xl border border-white/40">
        {totalCount === 0 ? (
          <Users className="h-12 w-12 text-slate-400" />
        ) : (
          <Users className="h-12 w-12 text-slate-400" />
        )}
      </div>
    </motion.div>

    <div className="text-center space-y-3 max-w-md">
      <motion.h3
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="text-xl font-bold text-slate-800"
      >
        No clinicians yet
      </motion.h3>
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="text-slate-600 leading-relaxed"
      >
        Get started by adding your first clinician to the practice.
      </motion.p>
    </div>
  </motion.div>
));

// Bulk Edit Modal Component
const BulkEditModal = React.memo<{
  isOpen: boolean;
  onClose: () => void;
  onSave: (updates: any) => Promise<void>;
  selectedCount: number;
}>(({ isOpen, onClose, onSave, selectedCount }) => {
  const [updates, setUpdates] = useState({
    status: '',
    ageRange: { min: '', max: '' },
    location: { city: '', state: '', zipCode: '' },
    insurance: [] as string[]
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Filter out empty values
      const filteredUpdates: any = {};
      if (updates.status) filteredUpdates.status = updates.status;
      if (updates.ageRange.min || updates.ageRange.max) {
        filteredUpdates.ageRange = {
          min: updates.ageRange.min ? parseInt(updates.ageRange.min) : undefined,
          max: updates.ageRange.max ? parseInt(updates.ageRange.max) : undefined
        };
      }
      if (updates.location.city || updates.location.state || updates.location.zipCode) {
        filteredUpdates.location = {
          city: updates.location.city,
          state: updates.location.state,
          zipCode: updates.location.zipCode
        };
      }
      if (updates.insurance.length > 0) {
        filteredUpdates.insurance = updates.insurance;
      }

      await onSave(filteredUpdates);
      onClose();
    } catch (error) {
      console.error('Bulk edit failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Bulk Edit Clinicians</h2>
              <p className="text-sm text-gray-600 mt-1">
                Editing {selectedCount} clinician{selectedCount !== 1 ? 's' : ''}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={updates.status}
                onChange={(e) => setUpdates(prev => ({ ...prev, status: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">No change</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="pending">Pending</option>
              </select>
            </div>

            {/* Age Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Age Range</label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Minimum Age</label>
                  <input
                    type="number"
                    value={updates.ageRange.min}
                    onChange={(e) => setUpdates(prev => ({
                      ...prev,
                      ageRange: { ...prev.ageRange, min: e.target.value }
                    }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Min age"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Maximum Age</label>
                  <input
                    type="number"
                    value={updates.ageRange.max}
                    onChange={(e) => setUpdates(prev => ({
                      ...prev,
                      ageRange: { ...prev.ageRange, max: e.target.value }
                    }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Max age"
                  />
                </div>
              </div>
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">City</label>
                  <input
                    type="text"
                    value={updates.location.city}
                    onChange={(e) => setUpdates(prev => ({
                      ...prev,
                      location: { ...prev.location, city: e.target.value }
                    }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="City"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">State</label>
                  <input
                    type="text"
                    value={updates.location.state}
                    onChange={(e) => setUpdates(prev => ({
                      ...prev,
                      location: { ...prev.location, state: e.target.value }
                    }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="State"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">ZIP Code</label>
                  <input
                    type="text"
                    value={updates.location.zipCode}
                    onChange={(e) => setUpdates(prev => ({
                      ...prev,
                      location: { ...prev.location, zipCode: e.target.value }
                    }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="ZIP"
                  />
                </div>
              </div>
            </div>

            {/* Insurance */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Insurance</label>
              <div className="grid grid-cols-2 gap-2">
                {['Aetna', 'Anthem', 'Blue Cross Blue Shield', 'Cigna', 'Humana', 'Kaiser Permanente', 'Medicaid', 'Medicare', 'UnitedHealth', 'Tricare', 'Self-Pay'].map((insurance) => (
                  <label key={insurance} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={updates.insurance.includes(insurance)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setUpdates(prev => ({
                            ...prev,
                            insurance: [...prev.insurance, insurance]
                          }));
                        } else {
                          setUpdates(prev => ({
                            ...prev,
                            insurance: prev.insurance.filter(i => i !== insurance)
                          }));
                        }
                      }}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{insurance}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {isSubmitting ? 'Updating...' : 'Update Clinicians'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
});

export default CliniciansPage;
